import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { SectionLabel } from "@/components/ui-extras";
import { Gem, Shirt, Sparkles, Heart, Camera, Sun, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — Cailève" },
      {
        name: "description",
        content:
          "Cailève is a luxury styling assistant that helps you curate jewelry, build complete looks, and receive personalized outfit recommendations.",
      },
    ],
  }),
  component: () => (
    <AppShell>
      <About />
    </AppShell>
  ),
});

const FEATURES = [
  {
    icon: Gem,
    title: "Your Jewelry Collection",
    description:
      "Catalog every piece with photos, category, color, material, storage location, and notes. Your entire collection — beautifully organized and always at your fingertips.",
  },
  {
    icon: Shirt,
    title: "Clothing & Accessories",
    description:
      "Add your clothing and accessories alongside your jewelry. Cailève sees your complete wardrobe and builds looks that bring all three together.",
  },
  {
    icon: Sparkles,
    title: "Style Me — Your Complete Look",
    description:
      "Tell Cailève about your moment and it composes a complete outfit from your own pieces — jewelry first, then clothing, accessories, and weather all considered. Save the result to your Lookbook.",
  },
  {
    icon: Heart,
    title: "The Pieces You Love Most",
    description:
      "Heart any piece or look to save it to your Favorites. The pieces you reach for most become the heart of your personal style archive.",
  },
  {
    icon: Camera,
    title: "Cailève Eye — In-Store Intelligence",
    description:
      "Photograph any piece you see anywhere and receive a personal verdict in seconds. Cailève reads your collection, identifies gaps and duplicates, and tells you whether a piece truly belongs in your life — even styled for the current weather.",
  },
  {
    icon: Sun,
    title: "Your Emotional Style Profile",
    description:
      "Cailève captures how each piece makes you feel — Confident, Elegant, Powerful, Romantic — and builds a style profile that makes every recommendation more personal over time.",
  },
];

function About() {
  return (
    <div className="max-w-5xl mx-auto px-8 bg-[var(--caileve-bg)]">
      {/* Hero */}
      <section className="pt-20 pb-16 text-center">
        <SectionLabel>About</SectionLabel>
        <h1 className="font-serif-display text-5xl md:text-7xl mt-6 leading-[1.05] text-[var(--caileve-ink)]">
          CAILÈVE
        </h1>
        <p className="font-serif-display italic text-2xl text-[var(--caileve-lilac)] mt-6">
          Style the pieces you already love.
        </p>
      </section>

      {/* Divider */}
      <div className="flex justify-center mb-16">
        <div className="w-16 h-px bg-[var(--caileve-copper)]" />
      </div>

      {/* Mission */}
      <section className="pb-20">
        <p
          className="font-serif-display text-xl md:text-2xl leading-relaxed text-[var(--caileve-ink)] max-w-3xl mx-auto text-center"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Cailève is the only app built around jewelry as the emotional center of personal style. It organizes your collection — jewelry, clothing, and accessories — builds complete looks from what you already own, and learns how each piece makes you feel. The more you use it, the more personal it becomes.
        </p>
      </section>

      {/* Contact */}
      <section className="pb-20">
        <div className="flex justify-center mb-10">
          <div className="w-16 h-px bg-[var(--caileve-copper)]" />
        </div>
        <h2 className="font-serif-display text-3xl text-center mb-10 text-[var(--caileve-ink)]">
          Contact
        </h2>
        <div className="max-w-md mx-auto space-y-5">
          <div className="flex items-center justify-between border-b border-[var(--caileve-soft)] pb-4">
            <span className="text-sm uppercase tracking-[0.25em] text-[var(--caileve-copper)]">Website</span>
            <a
              href="https://caileve.app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-copper)] transition-colors font-medium"
            >
              caileve.app
            </a>
          </div>
        <div className="flex items-center justify-between border-b border-[var(--caileve-soft)] pb-4">
            <span className="text-sm uppercase tracking-[0.25em] text-[var(--caileve-copper)]">Contact</span>
            <a
              href="mailto:hello@caileve.app"
              className="text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-copper)] transition-colors font-medium"
            >
              hello@caileve.app
            </a>
          </div>
          <p className="text-xs italic text-[var(--caileve-muted)]">We respond to every message within 24 hours.</p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="pb-24">
        {/* Trust & Privacy */}
        <div className="mb-16 rounded-md bg-[var(--caileve-soft)] p-8 md:p-10">
          <div className="flex items-center justify-center gap-3 mb-6">
            <ShieldCheck className="h-6 w-6 text-[var(--caileve-lilac)]" strokeWidth={1.5} />
            <h2 className="font-serif-display text-2xl md:text-3xl text-[var(--caileve-ink)]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
              Trust &amp; Privacy
            </h2>
          </div>
          <ul className="max-w-2xl mx-auto space-y-4 text-[var(--caileve-ink)]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            <li className="flex gap-3 text-lg leading-relaxed">
              <span className="text-[var(--caileve-lilac)] mt-1">•</span>
              <span>Your collection is private — only you can see your pieces, looks, and photos.</span>
            </li>
            <li className="flex gap-3 text-lg leading-relaxed">
              <span className="text-[var(--caileve-lilac)] mt-1">•</span>
              <span>Photos are stored in a private encrypted vault — never shared, never public.</span>
            </li>
            <li className="flex gap-3 text-lg leading-relaxed">
              <span className="text-[var(--caileve-lilac)] mt-1">•</span>
              <span>Your emotional style data belongs to you and is never sold or shared.</span>
            </li>
            <li className="flex gap-3 text-lg leading-relaxed">
              <span className="text-[var(--caileve-lilac)] mt-1">•</span>
              <span>Delete your account and all your data at any time from My Account.</span>
            </li>
          </ul>
        </div>
        <h2 className="font-serif-display text-3xl border-b border-[var(--caileve-copper)]/30 pb-4 mb-12 text-center text-[var(--caileve-ink)]">
          The Experience
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group p-8 border border-[var(--caileve-soft)] bg-white hover:border-[var(--caileve-lilac)] transition-colors"
            >
              <f.icon className="h-6 w-6 text-[var(--caileve-lilac)] mb-5" strokeWidth={1.5} />
              <h3 className="font-serif-display text-xl mb-3 text-[var(--caileve-ink)]">{f.title}</h3>
              <p className="text-sm text-[var(--caileve-ink)]/70 leading-relaxed">
                {f.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="pb-16 text-center">
        <div className="flex justify-center mb-8">
          <div className="w-16 h-px bg-[var(--caileve-copper)]" />
        </div>
        <p className="font-serif-display italic text-2xl text-[var(--caileve-lilac)] max-w-2xl mx-auto">
          "The piece comes first. It always has. Cailève simply remembers that."
        </p>
        <p className="text-xs uppercase tracking-[0.25em] text-[var(--caileve-muted)] mt-6">
          THE CAILÈVE PHILOSOPHY
        </p>
      </section>

      {/* Version + Copyright */}
      <section className="pb-12 text-center space-y-3">
        <p className="text-sm">
          <Link to="/privacy" className="text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-copper)] transition-colors">
            Privacy Policy
          </Link>
          <span className="mx-3 text-[var(--caileve-copper)]">·</span>
          <Link to="/terms" className="text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-copper)] transition-colors">
            Terms of Service
          </Link>
        </p>
        <p className="text-xs text-[var(--caileve-lilac-text)]">
          © 2026 Cailève. All rights reserved.
        </p>
      </section>

      <div
        className="max-w-3xl mx-auto px-6 py-16"
        style={{ borderTop: "1px solid var(--caileve-soft)" }}
      >
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-6">
          The story
        </p>
        <p
          className="font-serif-display text-2xl leading-relaxed"
          style={{ color: "var(--caileve-ink)" }}
        >
          Cailève was built for my wife, who had a beautiful jewelry collection she could never fully remember — and was not wearing nearly enough of.
        </p>
        <p
          className="font-serif-display italic text-xl mt-6 leading-relaxed"
          style={{ color: "var(--caileve-lilac)" }}
        >
          I am an academic, not a developer. I built this because the problem bothered me and I love her. I think a lot of women know this feeling.
        </p>
      </div>
    </div>
  );
}
